
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import Popup from "../../../ResuableCompoment/Popup";
import { fetchWalletBalance, fetchSettlementBalance, fetchAepsBalance } from "../../../Store/Slices/WalletSlice";
import { Plus, Minus } from "lucide-react"; // ✅ Added icons for the breakdown toggle

const Settlement1FundTransfer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { selectedBeneficiary = [], amount = 0 } = location.state || {};
    const beneficiary = selectedBeneficiary[0];
    const { lat, lng } = useSelector((s) => s.location);

    const remitterMobile = beneficiary?.remitterMobileNumber || "";

    const username = useSelector(
        (state) => state.auth.employeeDetails?.username
    );

    /* ---------------- UI & FORM STATE ---------------- */
    const [mpin, setMpin] = useState("");
    const mpinRefs = React.useRef([]);
    const [isLoading, setIsLoading] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [showTermsDetails, setShowTermsDetails] = useState(false);
    const [showBreakdown, setShowBreakdown] = useState(false); // ✅ Controls the breakdown visibility

    /* ---------------- FEE STATE ---------------- */
    const [fee, setFee] = useState(0);
    const [txnAmount, setTxnAmount] = useState(Number(amount));
    const [loadingFee, setLoadingFee] = useState(false);

    /* ---------------- IP ADDRESS ---------------- */
    const [ipAddress, setIpAddress] = useState("");

    useEffect(() => {
        fetch("https://api.ipify.org?format=json")
            .then((r) => r.json())
            .then((d) => setIpAddress(d.ip || ""))
            .catch(() => setIpAddress(""));
    }, []);

    // APIs
    const { callApi: soliteckPayout } = useApi("/soliteck/payout", "POST"); 
    const { callApi: calculateFee } = useApi("/dmt/fee/calculate/aeps/settle", "POST"); 

    /* ---------------- FEE CALCULATION EFFECT ---------------- */
    useEffect(() => {
        const fetchFee = async () => {
            if (!amount || Number(amount) <= 0) return;
            
            setLoadingFee(true);
            try {
                const payload = {
                    paymentMode: "IMPS",
                    provider: "SETTLEMENT",
                    sub_provider: "NIFI",
                    amount: Number(amount),
                    commercial_type: "TRANSACTION_FEE",
                    mid: username
                };
                
                const res = await calculateFee(payload);
                
                if (res && res.txn_amount) {
                    setTxnAmount(Number(res.txn_amount));
                    setFee(Number(res.fee) || 0);
                } else {
                    setTxnAmount(Number(amount));
                }
            } catch (err) {
                console.error("Fee calculation failed:", err);
                setTxnAmount(Number(amount));
            } finally {
                setLoadingFee(false);
            }
        };

        fetchFee();
    }, [amount, username]);

    const handleMpinChange = (index, value) => {
        if (!/^[0-9]?$/.test(value)) return;

        const newMpin = mpin.split("");
        newMpin[index] = value;
        const updated = newMpin.join("");
        setMpin(updated);

        if (value && index < 5) {
            mpinRefs.current[index + 1].focus();
        }
    };

    const handleMpinBackspace = (index, e) => {
        if (e.key === "Backspace" && !mpin[index] && index > 0) {
            mpinRefs.current[index - 1].focus();
        }
    };

    /* ---------------- SOLITECK PAYOUT EXECUTION ---------------- */
    const handleTransferAmount = async () => {
        if (!lat || !lng) {
            setPopupMessage("Please enable location access to proceed.");
            setPopupType("error");
            setShowPopup(true);
            return;
        }
        if (!mpin || mpin.length !== 6) {
            setPopupMessage("6-Digit MPIN is required");
            setPopupType("error");
            setShowPopup(true);
            return;
        }

        setIsLoading(true);

        try {
            const payload = {
                mid: username,
                mpin: mpin,
                beneficiaryName: beneficiary.beneficiaryName,
                beneficiaryAccount: beneficiary.accountNo,
                beneficiaryIfsc: beneficiary.ifscCode,
                beneficiaryBankName: beneficiary.bankName || "",
                beneId: beneficiary.bene_id || beneficiary.beneId || beneficiary.BeneId,
                beneficiaryMobile: remitterMobile, 
                netAmount: Number(amount),
                txnAmount: txnAmount, 
                mode: beneficiary.mode || "IMPS",
                customerMobile: remitterMobile, 
                lat: String(lat),
                lng: String(lng),
                ipAddress: ipAddress,
            };

            const res = await soliteckPayout(payload);

            if (res?.status === 202 || res?.error_code === "DMT050") {
                setPopupMessage(`❌ ${res?.message || "Invalid MPIN"}`);
                setPopupType("error");
                setShowPopup(true);
                setMpin(""); 
                return;
            }

            const status = res?.status || "FAILED";
            const message = res?.message || "Transaction failed";

            if (status === "SUCCESS") {
                const lines = [
                    "✅ Transaction Completed Successfully",
                    "",
                    `Amount: ₹${Number(res.amount || amount).toFixed(2)}`,
                    `Beneficiary: ${beneficiary.beneficiaryName}`,
                    `Account: ${beneficiary.accountNo}`,
                    `UTR: ${res.utr || "Pending"}`,
                    `Txn Ref: ${res.soliteckTxnRef || res.txnId}`
                ];
                setPopupMessage(lines.join("\n"));
                setPopupType("success");
                setShowPopup(true);
                return;
            }

            if (status === "PENDING") {
                const lines = [
                    "⏳ Transaction Pending",
                    "",
                    `Amount: ₹${Number(res.amount || amount).toFixed(2)}`,
                    `Beneficiary: ${beneficiary.beneficiaryName}`,
                    `Account: ${beneficiary.accountNo}`,
                    `Txn Ref: ${res.soliteckTxnRef || res.txnId}`,
                    "",
                    message 
                ];
                setPopupMessage(lines.join("\n"));
                setPopupType("pending");
                setShowPopup(true);
                return;
            }

            setPopupMessage(`❌ ${message}`);
            setPopupType("error");
            setShowPopup(true);

        } catch (err) {
            setPopupMessage(err.message || "Payout failed due to a server error.");
            setPopupType("error");
            setShowPopup(true);
        } finally {
            setIsLoading(false);
            dispatch(fetchWalletBalance());
            dispatch(fetchAepsBalance());
            dispatch(fetchSettlementBalance());
        }
    };

    if (!beneficiary) {
        return (
            <div className="p-6 text-center text-red-600 font-bold">
                Invalid access. Please select a beneficiary first.
            </div>
        );
    }

    return (
        <div className="max-w-[95%] mx-auto bg-white rounded-3xl shadow-lg px-4 py-3 ">
            <h2 className="text-2xl font-semibold mb-1 text-slate-800">
                 Fund Transfer
            </h2>

            {/* BENEFICIARY DETAILS */}
            <div className="mb-4 border rounded-xl p-4 2xl:p-6 min-[2000px]:p-8 bg-slate-50">
                <h3 className="text-lg 2xl:text-xl min-[2000px]:text-2xl font-semibold text-slate-700 mb-3">
                    Beneficiary Details
                </h3>

                <div className="text-base 2xl:text-xl min-[2000px]:text-xl space-y-1">
                    <div className="flex justify-between">
                        <span className="font-semibold text-slate-600">Name:</span>
                        <span className="font-bold text-slate-800">{beneficiary.beneficiaryName}</span>
                    </div>
                    <div className="flex justify-between items-center border-y py-2 my-2">
                        <span className="font-semibold text-slate-600">Account No:</span>
                        <span className="text-xl 2xl:text-2xl font-black text-red-600 tracking-widest">{beneficiary.accountNo}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-semibold text-slate-600">IFSC:</span>
                        <span className="font-bold text-slate-800">{beneficiary.ifscCode}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-semibold text-slate-600">Bank:</span>
                        <span className="font-bold text-slate-800">{beneficiary.bankName}</span>
                    </div>
                </div>
            </div>

            {/* TRANSFER AMOUNT & FEE BREAKDOWN (✅ UPDATED) */}
            <div className="border rounded-xl p-4 2xl:p-6 min-[2000px]:p-8 bg-slate-50 mb-6 transition-all duration-300">

                {/* Interactive Breakdown Trigger */}
                <div 
                    className="flex justify-between items-center cursor-pointer pb-4 border-slate-200  hover:opacity-80"
                    onClick={() => setShowBreakdown(!showBreakdown)}
                >
                    <span className="text-lg font-semibold text-slate-600 select-none">Amount Breakdown</span>
                    <button
                        type="button"
                        className="w-7 h-7 flex items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition-transform"
                    >
                        {showBreakdown ? <Minus size={16} /> : <Plus size={16} />}
                    </button>
                </div>

                {/* Always visible Transfer Amount */}
                <div className="flex justify-between items-center mb-1">
                    <span className="text-lg 2xl:text-xl font-bold text-slate-700">Transfer Amount</span>
                    <span className="font-bold text-2xl 2xl:text-3xl min-[2000px]:text-4xl text-green-600">
                        ₹ {Number(amount).toFixed(2)}
                    </span>
                </div>

                

                {/* Collapsible Fee Details */}
                {showBreakdown && (
                    <div className="mt-4 space-y-3 animate-[fadeIn_.2s_ease-out]">
                        {loadingFee ? (
                            <div className="text-sm text-slate-500 animate-pulse font-semibold">
                                Calculating applicable charges...
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-between items-center text-sm 2xl:text-base font-semibold text-slate-500">
                                    <span>Transaction Fee</span>
                                    <span>₹ {fee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center border-t border-slate-200 pt-3 mt-1">
                                    <span className="text-lg 2xl:text-xl font-extrabold text-slate-800">Total Debit</span>
                                    <span className="font-extrabold text-2xl 2xl:text-3xl min-[2000px]:text-4xl text-green-600">
                                        ₹ {txnAmount.toFixed(2)}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* MPIN INPUT */}
            <div className="">
                <label className="text-sm font-bold text-slate-700">
                    Enter MPIN <span className="text-red-500">*</span>
                </label>

                <div className="flex gap-2 sm:gap-3 mt-1">
                    {[...Array(6)].map((_, i) => (
                        <input
                            key={i}
                            ref={(el) => (mpinRefs.current[i] = el)}
                            type="password"
                            maxLength={1}
                            value={mpin[i] || ""}
                            onChange={(e) => handleMpinChange(i, e.target.value)}
                            onKeyDown={(e) => {
                                handleMpinBackspace(i, e);
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (!isLoading && !loadingFee && isTermsAccepted && !showPopup) {
                                        handleTransferAmount();
                                    }
                                }
                            }}
                            className="w-12 h-12 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:border-[#12345A] outline-none shadow-sm"
                        />
                    ))}
                </div>
            </div>

            {/* TERMS AND CONDITIONS */}
            <div className="mt-3 flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <input
                    type="checkbox"
                    id="terms"
                    checked={isTermsAccepted}
                    onChange={(e) => setIsTermsAccepted(e.target.checked)}
                    className="w-5 h-5 cursor-pointer accent-[#12345A]"
                />
                <label htmlFor="terms" className="text-sm sm:text-base text-slate-700 cursor-pointer select-none">
                    I have read, understood, and agree to the {" "}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowTermsDetails(true);
                        }}
                        className="text-indigo-600 font-bold hover:underline"
                    >
                        Declaration and Disclaimer
                    </button>
                </label>
            </div>

            {/* SUBMIT BUTTON */}
            <div className="flex justify-end mt-1">
                <button
                    onClick={handleTransferAmount}
                    disabled={isLoading || loadingFee || !isTermsAccepted || showPopup}
                    className={`w-full sm:w-auto px-12 py-4 rounded-2xl font-bold text-lg text-white transition-all duration-300 shadow-md
                        ${(isLoading || loadingFee || !isTermsAccepted || showPopup)
                            ? "bg-slate-300 text-slate-500 cursor-not-allowed pointer-events-none"
                            : "bg-[#12345A] hover:bg-[#0f2a4a] hover:shadow-lg active:scale-95"
                        }`}
                >
                    {isLoading ? "Processing Transfer..." : "Transfer Amount"}
                </button>
            </div>

            {/* RESPONSE POPUP */}
            {showPopup && (
                <Popup
                    message={popupMessage}
                    type={popupType}
                    // navigateTo={popupType === "error" ? null : "/transactionhistory"} 
                    navigateTo={popupType === "error" ? null : "/aepssettlement1imps2"} 
                    onClose={() => {
                        setShowPopup(false);
                        if (popupType === "success" || popupType === "pending") {
                            // navigate("/retailerdashboard");
                            navigate("/aepssettlement1imps2");
                        }
                    }}
                    showchildren={false}
                />
            )}

            {/* DETAILED TERMS MODAL */}
            {showTermsDetails && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 ">
                    <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg">TRANSACTIONAL DECLARATION</h3>
                            <button onClick={() => setShowTermsDetails(false)} className="text-2xl font-bold text-slate-500 hover:text-red-500">&times;</button>
                        </div>
                        <div className="p-6 overflow-y-auto text-sm text-slate-600 leading-relaxed space-y-4">
                            <p className="font-bold text-slate-800">By ticking the checkbox below and proceeding with this payment, I confirm, declare, and agree to the following representations:</p>
                            <ul className="list-decimal pl-5 space-y-2">
                                <li>I confirm that the funds used in this transaction are legitimately earned and entirely my own funds.</li>
                                <li>I confirm that the purpose of this transaction is entirely lawful and does not violate any applicable law.</li>
                                <li>I confirm that all personal and transactional information provided is true, accurate, and complete.</li>
                            </ul>
                            <p className="font-bold text-slate-800">
                                I have read, understood, and agree to BingoPay's Transactional Declaration.
                            </p>
                        </div>
                        <div className="p-4 border-t bg-slate-50 flex justify-end">
                            <button
                                onClick={() => {
                                    setIsTermsAccepted(true);
                                    setShowTermsDetails(false);
                                }}
                                className="bg-[#12345A] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#0f2a4a] transition-colors"
                            >
                                I Understand & Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settlement1FundTransfer;